import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
	UserCheck,
	LogOut,
	Menu,
	X,
	LucideProps,
} from "lucide-react";
import { Button } from "@/components/ui/button";

import MenuItem from "./MenuItem";
import MobileMenuItem from "./MobileMenuItem";
import styles from "./Header.module.css";

interface AdminHeaderProps {
	onHandleSignOut: () => void;
	navigation: { name: string; href: string; icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>> }[];
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ onHandleSignOut, navigation }) => {
	const navigate = useNavigate();

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const headerClasses = [
		styles.header,
		"bg-newTheme-cotton",
	];

	return (
		<header
			className={headerClasses.join(" ")}
			id={styles['header']}
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-0">
				<div className="flex justify-between items-center">
					{/* Left: Logo and Badge */}
					<div className="flex items-center space-x-4">
						<Link
							to="/admin-dashboard"
							className="flex items-center space-x-2"
						>
							<img
								className={styles.logo}
								src="/Gaia_Logo.svg"
								alt="GaiaBirth"
							/>
						</Link>

						<Button
							variant="ghost"
							size="sm"
							onClick={() => navigate("/admin-dashboard")}
							className={`${styles.doulaBtn} bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow`}
						>
							Admin Dashboard
						</Button>
					</div>

					{/* Center: Navigation Menu (Desktop) */}
					<ul className="hidden lg:flex items-center space-x-1">
						{navigation.map((item) => {
							return (
								<MenuItem
									key={item.name}
									title={item.name}
									href={item.href}
									icon={item.icon}
								/>
							);
						})}
					</ul>

					{/* Right: Action Buttons */}
					<div className="flex items-center space-x-3">
						{/* Sign Out Button */}
						<Button
							onClick={onHandleSignOut}
							variant="outline"
							className={`${styles.portalBtn} border-newTheme-lightGrey text-black hover:bg-gray-50 shadow-btn-shadow hover:text-black hidden lg:inline-flex`}
						>
							<LogOut className="w-4 h-4 mr-2" />
							<span className="hidden sm:inline">Sign Out</span>
						</Button>

						{/* Applications Button */}
						<Button
							onClick={() => navigate("/admin-applications")}
							className={`${styles.doulaBtn} bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow hidden lg:inline-flex`}
						>
							<UserCheck className="w-4 h-4 mr-2" />
							Applications
						</Button>

						{/* Mobile Menu Button */}
						<Button
							variant="ghost"
							size="sm"
							className="lg:hidden"
							onClick={() => {
								setMobileMenuOpen(!mobileMenuOpen);
							}}
						>
							{mobileMenuOpen ? (
								<X className="w-5 h-5" />
							) : (
								<Menu className="w-5 h-5" />
							)}
						</Button>
					</div>
				</div>
			</div>

			{/* Mobile Menu */}
			{mobileMenuOpen && (
				<nav className="lg:hidden backdrop-blur-md border-t border-sage-200 pt-4 mt-[22px]">
					<ul className="px-4 py-3 space-y-1">
						{navigation.map((item) => {
							return (
								<MobileMenuItem
									key={item.href}
									title={item.name}
									href={item.href}
									setIsMobileMenuOpen={setMobileMenuOpen}
									onMobileMenuToggle={setMobileMenuOpen}
								/>
							);
						})}

						{/* Mobile Applications Button */}
						<li>
							<Button
								onClick={() => {
									navigate("/admin-applications");
									setMobileMenuOpen(false);
								}}
								size="sm"
								className="md:w-full justify-start mt-3 bg-primary-500 hover:bg-primary-600 text-white shadow-lg"
							>
								<UserCheck className="w-4 h-4 mr-2" />
								Applications
							</Button>
						</li>
					</ul>
				</nav>
			)}
		</header>
	)
};

export default AdminHeader;